import ApiService from '@/services/apiService';
import { mapState, mapGetters } from 'vuex';
import scopeBreadcrumbMixin from '@/components/core/tenant/scopeBreadCrumbMixin';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import moment from 'moment';

const defaultItemsPerPage = 20;
const defaultTableOptions = {
  sortBy: ['email'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage,
};

export default {
  mixins: [scopeBreadcrumbMixin],
  props: {
    scopeIds: {
      type: Array,
      required: true,
    },
    includeAncestorAdmins: {
      type: Boolean,
      required: false,
    },
    includeSubAccounts: {
      type: Boolean,
      required: false,
    },
    searchTerm: {
      type: String,
      default: '',
    },
  },
  data: function () {
    return {
      loading: false,
      expandedItems: [],

      tableOptions: defaultTableOptions,
      tableFooterProps: {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [defaultItemsPerPage, 20, 50],
      },
      priorRequestOptions: null,
      serverItemsLength: 0,

      userList: [],

      deletePrompt: false,
      deletionModel: null,
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color', 'UiTheme', 'permissions']),
    ...mapState('authentication', ['user', 'isImpersonating']),
    ...mapGetters({
      sessionUserId: 'authentication/userId',
      sessionPrimaryTenantIds: 'authentication/primaryTenantIds',
      sessionAccess: 'authentication/access',
    }),
    userScopeOptions() {
      return [
        this.includeAncestorAdmins ? 'ancestors' : null,
        this.includeSubAccounts ? null : 'no-subtenants',
        'all',
      ].filter((o) => o);
    },
  },
  watch: {
    scopeIds: debounce(
      function () {
        this.fetchUsers();
      },
      400,
      { leading: true }
    ),
    searchTerm: debounce(
      function () {
        this.fetchUsers();
      },
      400,
      { leading: true }
    ),
    tableOptions: {
      handler() {
        this.fetchUsers();
      },
      deep: true,
    },
    includeAncestorAdmins: debounce(
      function () {
        this.fetchUsers();
      },
      400,
      { leading: true }
    ),
    includeSubAccounts: debounce(
      function () {
        this.fetchUsers();
      },
      400,
      { leading: true }
    ),
  },
  // disabled the auto-fetching of users here,
  // the `doFetchSessionAccessDetails` from userAccessPermissionMixin needs to have finnished before
  // calling this, so at the relevant place, synchronise those.
  // mounted() {
  //   this.fetchUsers();
  // },
  methods: {
    accessesForUser(item) {
      return get(item, 'access') || [];
    },
    fetchUsers() {
      const searchItem = (this.searchTerm || '').trim().length > 0 ? this.searchTerm.trim() : undefined;
      if (get(this.priorRequestOptions, 'search') !== searchItem) {
        // if the search-item is changed, reset to page 1
        this.tableOptions = { ...this.tableOptions, page: 1 };
      }

      const { sortBy, sortDesc, page, itemsPerPage } = this.tableOptions;
      const sortItem = sortBy && sortBy.length ? sortBy[0] : 'email';
      const doSortDesc = sortDesc && sortDesc.length ? sortDesc[0] : true;
      const sortDirection = doSortDesc ? 'DESC' : 'ASC';

      const usedPage = Math.max(1, page || 1);
      const usedItemsPerPage = itemsPerPage || defaultItemsPerPage;
      const offset = (usedPage - 1) * usedItemsPerPage;

      const newRequestOptions = {
        scopeIds: this.scopeIds,
        scopeOptions: this.userScopeOptions,
        offset,
        limit: usedItemsPerPage,
        order: { variable: sortItem, direction: sortDirection },
        search: searchItem,
      };
      if (isEqual(newRequestOptions, this.priorRequestOptions)) {
        return;
      }
      this.priorRequestOptions = newRequestOptions;

      this.loading = true;
      return ApiService.fetchUsers(
        newRequestOptions.scopeIds,
        newRequestOptions.scopeOptions,
        this.fetchOptions,
        newRequestOptions.offset,
        newRequestOptions.limit,
        newRequestOptions.order,
        newRequestOptions.search
      )
        .then(async (data) => {
          // fetch all scope-details for all accesses of all users
          // this includes all ancestor-info for all scopes as well
          const allUsersScopes = uniq(
            flatten((data.users || []).map((user) => this.accessesForUser(user).map((access) => access.scopeId)))
          );
          await Promise.all(
            allUsersScopes.map((scopeId) => {
              return this.fetchScopeDetails({ id: scopeId });
            })
          ).catch((error) => {
            console.error(error);
          });
          this.serverItemsLength = data.totalCount || 0;
          this.userList = data.users || [];
          this.userList.forEach((user) => {
            if (!user.properties) {
              user.properties = { address: {} };
            }
          });
        })
        .catch((error) => {
          if (get(error, 'response.status') === 401) {
            if (error.response.data.message.startsWith('NO_PERMISSION_')) {
              const rule = error.response.data.message.replace('NO_PERMISSION_', '').split('.');
              if (rule.length >= 2) {
                const subject = this.$i18n_t(`Error.permissions.${rule[0]}`);
                const action = this.$i18n_t(`Error.permissions.${rule[1]}`);
                this.$snack(this.$i18n_t('Error.NO_PERMISSION_', { subject, action }), 'error');
                return;
              }
            }
          }
          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack(get(error, 'response.data.message', this.$i18n_t('Common.usersFetchFail')), 'error');
          }
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    doDeleteUser(model) {
      ApiService.deleteUser(model.id)
        .then(() => {
          this.$snack(
            `${this.$i18n_t('accMgmt.user.user')}(ID: ${model.id}) ${this.$i18n_t('accMgmt.user.beenDeleted')}`,
            'success'
          );
        })
        .catch((error) => {
          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack(get(error, 'response.data.message', 'COULD NOT DELETE API KEY'), 'error');
          }
        })
        .finally(() => {
          // reset request-option to make sure the table(page) is reloaded
          this.priorRequestOptions = null;
          // this.fetchUsers();
          this.filterUserList(model);
        });
    },
    clickedUserTableRow(item) {
      if (this.expandedItems.length === 0 || this.expandedItems[0].id !== item.id) {
        if (
          !item.hasLoadedAccess &&
          (!(get(item, 'access') || []).length || !item.access.filter((a) => a && a.scopeId).length)
        ) {
          ApiService.getUser(this, item.id)
            .then((data) => {
              item.access = data.access.filter((a) => a && a.scopeId);
              item.hasLoadedAccess = true;
            })
            .catch((error) => {
              if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
                this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
              } else {
                this.$snack(get(error, 'response.data.message', this.$i18n_t('Common.usersFetchFail')), 'error');
              }
              console.error(error);
            });
        }
        this.expandedItems = [item];
      } else {
        this.expandedItems = [];
      }
    },
    localizeDate(a) {
      if (a) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(a).format('l');
      }
      return '-';
    },
    filterUserList(model) {
      this.userList = this.userList.filter((item) => item.id !== model.id);
    },
    setCurrentTablePage(value) {
      this.tableOptions.page = value;
    },
  },
};
